/**
 * Validation Service
 * Handles form validation and error messages
 */

import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { VALIDATION_PATTERNS, FORM_CONFIG } from '../constants';

/**
 * Service for validation logic and error messages
 */
@Injectable({ providedIn: 'root' })
export class ValidationService {
  /**
   * Get validation pattern by name
   * @param patternName The pattern name (email, phone, pan, etc.)
   * @returns RegExp pattern or undefined
   */
  getPattern(patternName: string): RegExp | undefined {
    const validationPattern = VALIDATION_PATTERNS[patternName.toLowerCase()];
    return validationPattern?.pattern;
  }
  
  /**
   * Get error message for a pattern
   * @param patternName The pattern name
   * @returns Error message string
   */
  getPatternMessage(patternName: string): string {
    const validationPattern = VALIDATION_PATTERNS[patternName.toLowerCase()];
    return validationPattern?.message || 'Invalid format';
  }
  
  /**
   * Get all error messages for a control
   * @param control The form control
   * @param fieldLabel The field label for error messages
   * @returns Array of error messages
   */
  getErrorMessages(control: AbstractControl, fieldLabel: string): string[] {
    const errors: string[] = [];
    
    if (!control || !control.errors) {
      return errors;
    }
    
    const controlErrors: ValidationErrors = control.errors;
    
    // Required error
    if (controlErrors['required']) {
      errors.push(`${fieldLabel} is required`);
    }
    
    // Email error
    if (controlErrors['email']) {
      errors.push(`${fieldLabel} must be a valid email address`);
    }
    
    // Pattern error
    if (controlErrors['pattern']) {
      errors.push(`${fieldLabel} has an invalid format`);
    }
    
    // Min error
    if (controlErrors['min']) {
      errors.push(`${fieldLabel} must be at least ${controlErrors['min'].min}`);
    }
    
    // Max error
    if (controlErrors['max']) {
      errors.push(`${fieldLabel} must be at most ${controlErrors['max'].max}`);
    }
    
    // Min length error
    if (controlErrors['minlength']) {
      errors.push(`${fieldLabel} must be at least ${controlErrors['minlength'].requiredLength} characters`);
    }
    
    // Max length error
    if (controlErrors['maxlength']) {
      errors.push(`${fieldLabel} can be at most ${controlErrors['maxlength'].requiredLength} characters`);
    }
    
    // Custom errors
    Object.keys(controlErrors).forEach(key => {
      if (!['required', 'email', 'pattern', 'min', 'max', 'minlength', 'maxlength'].includes(key)) {
        errors.push(controlErrors[key]);
      }
    });
    
    return errors;
  }
  
  /**
   * Get single error message for a control
   * @param control The form control
   * @param fieldLabel The field label
   * @returns First error message or empty string
   */
  getFirstErrorMessage(control: AbstractControl, fieldLabel: string): string {
    const errors = this.getErrorMessages(control, fieldLabel);
    return errors.length > 0 ? errors[0] : '';
  }
  
  /**
   * Check if control has specific error
   * @param control The form control
   * @param errorKey The error key to check
   * @returns true if control has the error
   */
  hasError(control: AbstractControl, errorKey: string): boolean {
    return control?.hasError(errorKey) || false;
  }
  
  /**
   * Check if control should show errors
   * @param control The form control
   * @returns true if errors should be displayed
   */
  shouldShowErrors(control: AbstractControl): boolean {
    if (!control) return false;
    
    if (FORM_CONFIG.SHOW_ERRORS_IMMEDIATELY) {
      return control.invalid && control.dirty;
    }
    
    return control.invalid && control.touched;
  }
}
